import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { meetingRequiresAuthentication } from "@/lib/productHasPrice";
import { fetchLeadSession } from "../leadSessionClient";
import type { MeetingProduct } from "./meetingProductTypes";

export type MeetingViewerAuthStatus = "loading" | "allowed" | "login";

export function useMeetingViewerAuth(): MeetingViewerAuthStatus {
  const { id: productId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isStaffJoinLink =
    searchParams.get("host") === "1" || searchParams.get("admin") === "1";
  const [status, setStatus] = useState<MeetingViewerAuthStatus>("loading");

  useEffect(() => {
    if (!productId || isStaffJoinLink) {
      setStatus("allowed");
      return;
    }

    let cancelled = false;

    const check = async () => {
      setStatus("loading");
      try {
        const productRes = await fetch(`/api/products/${productId}`);
        if (!productRes.ok) {
          if (!cancelled) setStatus("allowed");
          return;
        }

        const productData = (await productRes.json()) as { product?: MeetingProduct };
        const product = productData.product;

        if (!product || !meetingRequiresAuthentication(product)) {
          if (!cancelled) setStatus("allowed");
          return;
        }

        const authRes = await fetchLeadSession({ productId });
        const authData = (await authRes.json()) as { authenticated?: boolean };

        if (!cancelled) {
          setStatus(authData.authenticated ? "allowed" : "login");
        }
      } catch {
        if (!cancelled) setStatus("allowed");
      }
    };

    void check();

    return () => {
      cancelled = true;
    };
  }, [productId, isStaffJoinLink]);

  return status;
}
